import { Check } from "@phosphor-icons/react";
import { useSeo, useSite, usePublic } from "@/hooks/useSite";
import { Band, Heading, PageHero, SitePage, StoreButtons, money } from "@/components/site/SiteBits";
import { humanize } from "@/lib/format";

const PERIODS = [["week", "1 week"], ["month", "1 month"], ["3mo", "3 months"], ["6mo", "6 months"]];
const section = (doc, key) => doc?.sections?.find((s) => (s.heading || "").toLowerCase() === key);
const points = (f, k) => [1, 2, 3, 4].map((i) => f[`${k}_${i}`]).filter(Boolean);

function Points({ items, dark }) {
  return (
    <ul className="mt-6 space-y-3 text-[0.9375rem]">
      {items.map((p, i) => <li key={i} className="flex gap-2.5"><Check size={18} weight="bold" className={`mt-0.5 shrink-0 ${dark ? "text-[#F07A5C]" : "s-coral"}`} /><span>{p}</span></li>)}
    </ul>
  );
}

function FreeCard({ f, name }) {
  return (
    <li className="s-card flex flex-col p-7" data-testid="plan-free">
      <h3 className="text-lg font-bold">{name}</h3>
      <p className="s-muted mt-1 text-sm">{f.free_title}</p>
      <p className="mt-6 text-4xl font-bold tracking-tight">{f.free_price}</p>
      <p className="s-muted text-sm">{f.free_price_note}</p>
      <Points items={points(f, "free")} />
    </li>
  );
}

function PaidCard({ tier, f, copy, subs }) {
  const duo = tier === "duo";
  const rows = PERIODS.map(([p, label]) => ({ label, s: subs.find((x) => x.period === p && x.active) })).filter((r) => r.s);
  return (
    <li className={`flex flex-col rounded-[20px] p-7 ${duo ? "plan-duo" : "s-card"}`} data-testid={`plan-${tier}`}>
      <div className="flex items-center justify-between gap-2"><h3 className="text-lg font-bold">{copy?.heading || humanize(tier)}</h3>
        {duo && f.duo_tag && <span className="s-soon">{f.duo_tag}</span>}</div>
      <p className={`mt-1 text-sm ${duo ? "plan-duo-muted" : "s-muted"}`}>{duo ? f.duo_tagline : copy?.body}</p>
      <ul className={`mt-6 divide-y ${duo ? "divide-white/10" : "s-line divide-[var(--s-line)]"}`} data-testid={`plan-${tier}-prices`}>
        {rows.map(({ label, s }) => (
          <li key={s.period} className="flex items-baseline justify-between gap-3 py-2.5">
            <span className="text-sm">{label}</span>
            <span className="text-right"><b className="tabular-nums">{money(s.price_usd)}</b>
              {s.period !== "week" && s.per_week_usd != null && f.per_week && <span className={`block text-xs tabular-nums ${duo ? "plan-duo-muted" : "s-muted"}`}>{f.per_week.replace("{price}", money(s.per_week_usd))}</span>}</span>
          </li>))}
      </ul>
      <Points items={points(f, tier)} dark={duo} />
    </li>
  );
}

function Packs({ f, packs }) {
  const groups = [["free", f.packs_group_free], ["lane", f.packs_group_lane], ["duo", f.packs_group_duo]];
  return (
    <section className="mt-24" data-testid="plans-packs">
      <Heading title={f.packs_title} accent={f.packs_accent} />
      <ul className="mt-10 grid gap-5 md:grid-cols-3">
        {groups.map(([k, label]) => {
          const list = (packs?.[k] || packs?.[k === "lane" ? "single_lane" : k] || []).filter((p) => p.active).sort((a, b) => a.quantity - b.quantity);
          return (
            <li key={k} className="s-card p-7" data-testid={`plans-pack-${k}`}>
              <h3 className="font-bold">{label}</h3>
              <ul className="mt-4 space-y-2.5">{list.map((p) => <li key={p.quantity} className="s-card-2 flex items-center justify-between px-4 py-3 text-sm"><span>× {p.quantity}</span><b className="tabular-nums">{money(p.price_usd)}</b></li>)}</ul>
            </li>);
        })}
      </ul>
      {f.packs_foot && <p className="s-muted mt-5 text-sm">{f.packs_foot}</p>}
    </section>
  );
}

const Cell = ({ v }) => (v === true ? <Check size={18} weight="bold" className="s-coral mx-auto" aria-label="Yes" /> : v === false ? <span className="s-muted" aria-label="No">–</span> : <span>{v}</span>);
function Compare({ f, compare, names }) {
  if (!compare?.rows?.length) return null;
  return (
    <section className="mt-24" data-testid="plans-compare">
      <Heading title={f.compare_title} accent={f.compare_accent} />
      <div className="s-card mt-10 overflow-x-auto p-2 sm:p-4" data-testid="plans-compare-scroll">
        <table className="w-full min-w-[640px] text-sm">
          <thead><tr><th className="p-3 text-left font-medium"><span className="sr-only">Feature</span></th>
            {compare.tiers.map((t) => <th key={t} scope="col" className={`p-3 text-center font-bold ${t === "duo" ? "s-coral" : ""}`}>{names[t] || humanize(t)}</th>)}</tr></thead>
          <tbody>{compare.rows.map((r) => (
            <tr key={r.key} className="border-t s-line" data-testid={`plans-compare-row-${r.key}`}>
              <th scope="row" className="s-muted p-3 text-left font-medium">{r.label === r.key ? humanize(r.key) : r.label}</th>
              {compare.tiers.map((t) => <td key={t} className="p-3 text-center"><Cell v={r.values?.[t]} /></td>)}
            </tr>))}</tbody>
        </table>
      </div>
    </section>
  );
}

export default function Plans() {
  const site = useSite();
  const pricing = usePublic("/public/pricing");
  const f = site.data?.docs?.site_plans?.fields || {};
  const doc = site.data?.docs?.plans_copy;
  const p = pricing.data;
  const names = Object.fromEntries(["free", "crushr", "winkr", "duo"].map((t) => [t, section(doc, t)?.heading]));
  useSeo("Plans", f.hero_lead);
  return (
    <SitePage loading={site.loading || pricing.loading} error={site.error || pricing.error} onRetry={() => { site.reload(); pricing.reload(); }} testId="plans-page">
      <PageHero chip={f.hero_chip} title={f.hero_title} accent={f.hero_accent} lead={f.hero_lead} testId="plans-hero" />
      <ul className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="plans-cards">
        <FreeCard f={f} name={names.free || humanize("free")} />
        {["crushr", "winkr", "duo"].map((t) => <PaidCard key={t} tier={t} f={f} copy={section(doc, t)} subs={(p?.subscriptions || []).filter((s) => s.tier === t)} />)}
      </ul>
      {p?.packs_enabled && <Packs f={f} packs={p.packs} />}
      <Compare f={f} compare={site.data?.plans_compare} names={names} />
      <div className="mt-16 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between" data-testid="plans-bought-in-app">
        <div><p className="font-semibold">{f.bought_in_app}</p>{f.fine_print && <p className="s-muted mt-2 max-w-2xl text-sm leading-6" data-testid="plans-fine-print">{f.fine_print}</p>}</div>
        <StoreButtons stores={site.data?.stores} testId="plans-stores" />
      </div>
      <Band title={f.band_title} accent={f.band_accent} line={f.band_line} button={f.band_button} testId="plans-band" />
    </SitePage>
  );
}
